import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import { ExtraHeaders, useApiQuery } from 'src/api';
import { ObjectMap, useApiGet } from 'src/api/api-hooks';
import { useBuildVars } from 'src/config';
import {
  useQueryParams,
  parseResponse,
  QueryResult,
  PaginationResult,
} from './hooks';

const CUSTOMERS_URL = '/customers';

export const getGroupCustomersUrl = (id: string) => `/${id}${CUSTOMERS_URL}`;

export const getDownloadGroupCustomersUrl = (id: string) =>
  `${getGroupCustomersUrl(id)}.xlsx`;

export const CustomerSearchFilters = [
  'customerName',
  'customerNumber',
  'masterCustomerName',
  'masterCustomerNumber',
] as const;
export type CustomerSearchSearchFilter = typeof CustomerSearchFilters[number];

export type CustomerListResult = QueryResult<Customer>;
export interface Customer {
  customerName: string;
  customerNumber: string;
  id: string;
  masterCustomerName: string;
  masterCustomerNumber: string;
  modified?: string;
  modifiedBy?: string;
}

export function useCustomerQueryParams({ id, pageSize }: any = {}) {
  const { searchQuery, ...rest } = useQueryParams<CustomerSearchSearchFilter>({
    filter: {
      field: CustomerSearchFilters[0],
      value: '',
    },
    pageSize: pageSize || 25,
  });

  return {
    searchQuery: {
      ...searchQuery,
      ...(id && { id }),
    },
    ...rest,
  };
}

export function useCustomerSearchQuery({
  countryCode,
  id,
  page,
  pageSize,
  ...query
}: any) {
  const buildVars = useBuildVars();
  return useApiQuery<CustomerListResult>(
    id ? getGroupCustomersUrl(id) : CUSTOMERS_URL,
    {
      ...query,
      countryCode,
      page,
      pageSize,
    },
    {
      enabled:
        Object.values(query as Record<string, string>).reduce(
          (acc, value) => acc + value.length,
          0
        ) > 1 || !!id,
    },
    undefined,
    {
      host: buildVars.CG_API_BASE,
      parseResponse: (res) => parseResponse<Customer>(res),
    }
  );
}

export function useBulkCustomers() {
  const pageSize = 100;
  const buildVars = useBuildVars();
  const apiGet = useApiGet({
    host: buildVars.CG_API_BASE,
    parseResponse: (res) => parseResponse<Customer>(res),
  });
  const { searchQuery } = useCustomerQueryParams({ pageSize });

  return (customerNumbers: string[]) => {
    const chunksCustomerNumbers = chunk([...customerNumbers], pageSize);
    return Promise.all(
      chunksCustomerNumbers.map((_customerNumbers) =>
        apiGet(CUSTOMERS_URL, {
          ...searchQuery,
          customerNumbers: _customerNumbers,
        })
          .then((res) => res.list)
          .catch(() => [])
      )
    ).then((res) => flatten(res));
  };
}

const getApiLogic = (
  apiGet: (
    route: string,
    params?: ObjectMap,
    extraHeaders?: ExtraHeaders
  ) => Promise<{
    list: any;
    pagination: PaginationResult;
  }>,
  chunksCustomerNumbers: string[][],
  searchQuery: any
) =>
  Promise.all(
    chunksCustomerNumbers.map((_masterCustomerNumbers) => {
      function recursiveGet({
        list: acc = [],
        pagination: { currentPage = 0, hasNext = true } = {},
      }: { list?: any[]; pagination?: any } = {}): any {
        return hasNext
          ? apiGet(CUSTOMERS_URL, {
              ...searchQuery,
              masterCustomerNumbers: _masterCustomerNumbers,
              page: currentPage + 1,
            }).then(({ list, pagination }) =>
              recursiveGet({ list: [...list, ...acc], pagination })
            )
          : acc;
      }
      return recursiveGet().catch(() => []);
    })
  ).then((res) => flatten(res));

export function useGetRelated() {
  const pageSize = 100;
  const buildVars = useBuildVars();
  const apiGet = useApiGet({
    host: buildVars.CG_API_BASE,
    parseResponse: (res) => parseResponse<Customer>(res),
  });
  const { searchQuery } = useCustomerQueryParams({ pageSize });

  return (numbers: string[]) => {
    const chunksCustomerNumbers = chunk([...numbers], pageSize);
    return getApiLogic(apiGet, chunksCustomerNumbers, searchQuery);
  };
}

export function useGetAllCustomerGroupMembers() {
  const pageSize = 100;
  const buildVars = useBuildVars();
  const apiGet = useApiGet({
    host: buildVars.CG_API_BASE,
    parseResponse: (res) => parseResponse<Customer>(res),
  });
  const { searchQuery } = useCustomerQueryParams({ pageSize });

  return (customerGroupId: string): Promise<Customer[]> => {
    function recursiveGet({
      list: acc = [],
      pagination: { currentPage = 0, hasNext = true } = {},
    }: { list?: any[]; pagination?: any } = {}): any {
      return hasNext
        ? apiGet(getGroupCustomersUrl(customerGroupId), {
            ...searchQuery,
            page: currentPage + 1,
          }).then(({ list, pagination }) =>
            recursiveGet({ list: [...list, ...acc], pagination })
          )
        : acc;
    }
    return recursiveGet().catch(() => []);
  };
}
